/**
 * Doc-kind taxonomy. Single source of truth for the FE — everything else
 * (label maps, icon maps, color maps, filter buckets) is derived from
 * the `DOC_KINDS` array below. Adding or removing a kind here updates
 * every consumer.
 *
 * Hex colors are intentional per-kind shades (not theme tokens) for
 * at-a-glance recognition; if/when the theme adds a doc-kind palette,
 * swap these for `theme.palette.docKind.*`.
 *
 * The canonical doc_kind string vocabulary is set BE-side by the
 * Fast Card extraction prompt (see
 * `services/ehr_api/lib/modules/document_processing/prompts/processing_prompts.py`).
 * If a value is missing from this file, the FE will fall back to
 * `other` styling for it.
 */

import type { SvgIconComponent } from '@mui/icons-material';
import ArticleOutlined from '@mui/icons-material/ArticleOutlined';
import AssignmentOutlined from '@mui/icons-material/AssignmentOutlined';
import BadgeOutlined from '@mui/icons-material/BadgeOutlined';
import BiotechOutlined from '@mui/icons-material/BiotechOutlined';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import EditNoteOutlined from '@mui/icons-material/EditNoteOutlined';
import FolderOutlined from '@mui/icons-material/FolderOutlined';
import ForwardOutlined from '@mui/icons-material/ForwardOutlined';
import GavelOutlined from '@mui/icons-material/GavelOutlined';
import ImageOutlined from '@mui/icons-material/ImageOutlined';
import ListAltOutlined from '@mui/icons-material/ListAltOutlined';
import MedicalServicesOutlined from '@mui/icons-material/MedicalServicesOutlined';
import PsychologyOutlined from '@mui/icons-material/PsychologyOutlined';
import QuizOutlined from '@mui/icons-material/QuizOutlined';
import ScienceOutlined from '@mui/icons-material/ScienceOutlined';
import ShieldOutlined from '@mui/icons-material/ShieldOutlined';
import VerifiedOutlined from '@mui/icons-material/VerifiedOutlined';

export interface KindColor {
  /** Foreground (icon + label text) — opaque */
  fg: string;
  /** Background tint (chip/badge) — already alpha-blended */
  bg: string;
}

interface DocKindDef {
  /** The literal `doc_kind` string the BE emits. */
  value: string;
  /** Display label (lowercase; capitalize at the call site for chips). */
  label: string;
  icon: SvgIconComponent;
  color: KindColor;
  /**
   * Filter-strip bucket id this kind rolls up into (see KIND_FILTERS).
   * Multiple kinds may share a bucket; a kind with no bucket is reachable
   * only through the catch-all "All" filter.
   */
  bucket?: string;
}

/**
 * The single canonical list of FE-known doc_kind values. Add/remove an
 * entry here to add/remove a kind everywhere — labels, icons, colors,
 * filter buckets all derive from this.
 */
const DOC_KINDS: DocKindDef[] = [
  {
    value: 'clinical_note',
    label: 'clinical note',
    icon: DescriptionOutlined,
    color: { fg: '#92400E', bg: 'rgba(146,64,14,0.10)' },
    bucket: 'notes',
  },
  {
    value: 'markdown_doc',
    label: 'session notes',
    icon: EditNoteOutlined,
    color: { fg: '#1C1E85', bg: 'rgba(28,30,133,0.08)' },
    bucket: 'notes',
  },
  {
    value: 'lab_report',
    label: 'lab report',
    icon: ScienceOutlined,
    color: { fg: '#047857', bg: 'rgba(4,120,85,0.10)' },
    bucket: 'labs',
  },
  {
    value: 'lab_order',
    label: 'lab order',
    icon: BiotechOutlined,
    color: { fg: '#0E7490', bg: 'rgba(14,116,144,0.10)' },
    bucket: 'labs',
  },
  {
    value: 'discharge_summary',
    label: 'discharge',
    icon: MedicalServicesOutlined,
    color: { fg: '#B45309', bg: 'rgba(180,83,9,0.12)' },
    bucket: 'notes',
  },
  {
    value: 'neuropsych_eval',
    label: 'neuropsych eval',
    icon: PsychologyOutlined,
    color: { fg: '#0E7490', bg: 'rgba(14,116,144,0.10)' },
    bucket: 'notes',
  },
  {
    value: 'assessment',
    label: 'assessment',
    icon: QuizOutlined,
    color: { fg: '#6D28D9', bg: 'rgba(109,40,217,0.10)' },
    bucket: 'notes',
  },
  {
    value: 'imaging',
    label: 'imaging',
    icon: ImageOutlined,
    color: { fg: '#6D28D9', bg: 'rgba(109,40,217,0.10)' },
    bucket: 'notes',
  },
  {
    value: 'form',
    label: 'form',
    icon: ListAltOutlined,
    color: { fg: '#0369A1', bg: 'rgba(3,105,161,0.10)' },
    bucket: 'forms',
  },
  {
    value: 'intake_form',
    label: 'intake form',
    icon: AssignmentOutlined,
    color: { fg: '#0369A1', bg: 'rgba(3,105,161,0.10)' },
    bucket: 'forms',
  },
  // ── Admin: everything that isn't a clinical note, lab, or form ──
  {
    value: 'roi',
    label: 'ROI',
    icon: ShieldOutlined,
    color: { fg: '#047857', bg: 'rgba(4,120,85,0.10)' },
    bucket: 'admin',
  },
  {
    value: 'consent',
    label: 'consent',
    icon: VerifiedOutlined,
    color: { fg: '#047857', bg: 'rgba(4,120,85,0.10)' },
    bucket: 'admin',
  },
  {
    value: 'insurance_card',
    label: 'insurance',
    icon: BadgeOutlined,
    color: { fg: '#4B5563', bg: 'rgba(75,85,99,0.10)' },
    bucket: 'admin',
  },
  {
    value: 'referral',
    label: 'referral',
    icon: ForwardOutlined,
    color: { fg: '#BE185D', bg: 'rgba(190,24,93,0.10)' },
    bucket: 'admin',
  },
  {
    value: 'legal_document',
    label: 'legal',
    icon: GavelOutlined,
    color: { fg: '#374151', bg: 'rgba(55,65,81,0.10)' },
    bucket: 'admin',
  },
  {
    value: 'prior_records_packet',
    label: 'records packet',
    icon: FolderOutlined,
    color: { fg: '#1E125E', bg: 'rgba(30,18,94,0.10)' },
    bucket: 'admin',
  },
  {
    value: 'other',
    label: 'document',
    icon: ArticleOutlined,
    color: { fg: '#6B7280', bg: 'rgba(107,114,128,0.10)' },
    bucket: 'admin',
  },
];

// ── Derived maps (don't edit directly — edit DOC_KINDS above) ────────

export const KIND_LABEL: Record<string, string> = Object.fromEntries(
  DOC_KINDS.map(k => [k.value, k.label]),
);

export const KIND_ICON: Record<string, SvgIconComponent> = Object.fromEntries(
  DOC_KINDS.map(k => [k.value, k.icon]),
);

export const KIND_COLOR: Record<string, KindColor> = Object.fromEntries(
  DOC_KINDS.map(k => [k.value, k.color]),
);

export function kindStyle(kind: string | null | undefined): KindColor {
  return KIND_COLOR[kind ?? 'other'] ?? KIND_COLOR.other;
}

export function kindIcon(kind: string | null | undefined): SvgIconComponent {
  return KIND_ICON[kind ?? 'other'] ?? ArticleOutlined;
}

export function kindLabel(kind: string | null | undefined): string {
  return KIND_LABEL[kind ?? 'other'] ?? 'document';
}

// ── Filter buckets ───────────────────────────────────────────────────

export interface KindFilter {
  id: string;
  label: string;
  /** null = matches all kinds (the "All" bucket). */
  kinds: string[] | null;
}

/**
 * Bucket display order + labels. Adding a new bucket requires both an
 * entry here AND at least one DOC_KINDS entry whose `bucket` matches the
 * id. Pruning a bucket: remove the entry here AND clear `bucket` from
 * any DOC_KINDS entries that pointed to it.
 */
const BUCKETS: { id: string; label: string }[] = [
  { id: 'notes', label: 'Notes' },
  { id: 'labs', label: 'Labs' },
  { id: 'forms', label: 'Forms' },
  { id: 'admin', label: 'Admin' },
];

/**
 * Filter buckets, derived from DOC_KINDS' `bucket` field. The "All"
 * bucket is prepended; per-bucket `kinds` lists are auto-populated.
 * Empty buckets are dropped so filter strips don't show dead chips.
 */
export const KIND_FILTERS: KindFilter[] = [
  { id: 'all', label: 'All', kinds: null },
  ...BUCKETS.map(b => ({
    id: b.id,
    label: b.label,
    kinds: DOC_KINDS.filter(k => k.bucket === b.id).map(k => k.value),
  })).filter(b => (b.kinds?.length ?? 0) > 0),
];
